import { useMemo, useState } from 'react'

import { Button } from '@/ui/Button'
import { Popup } from '@/ui/Popup'
import { Input } from '@/ui/Input'
import { useGetHubs } from '../../api/useGetHubs'
import { ModalTypes, useModal } from '@/components/Modal'
import { HubRoute } from '@/types/hub'
import { useWorkstationStore } from '../../store'
import { sortRoutes } from '../../helpers/sortRoutes'

import styles from './ReassignRouteAction.module.scss'

import PlusIcon from '../../assets/plus.svg'
import ChevronRight from '../../assets/chevron-right.svg'

export const ReassignRouteAction = () => {
    const [search, setSearch] = useState('')
    const { workstation } = useWorkstationStore()
    const { data: hubs = [] } = useGetHubs()
    const { openModal } = useModal()

    const routesToReassign = useMemo(() => {
        if (!hubs.length) {
            return []
        }

        const routesWithWorkstations = hubs.reduce<(HubRoute & { workstation: string })[]>((acc, item) => {
            const workstations = item.workstations.filter(
                (station) => station.workstationNumber !== workstation?.workstationNumber
            )

            return [
                ...acc,
                ...workstations.flatMap((station) =>
                    station.routes.reduce<(HubRoute & { workstation: string })[]>((acc, item) => {
                        const isReassignedRoute = station.routeReassignments.find((route) => route.route === item.route)

                        return isReassignedRoute ? acc : [...acc, { ...item, workstation: station.workstationNumber }]
                    }, [])
                ),
            ]
        }, [])

        sortRoutes(routesWithWorkstations)

        return routesWithWorkstations.reduce<{ route: string; workstation: string }[]>((acc, route) => {
            const applied = route.route.toLocaleLowerCase().includes(search.toLocaleLowerCase())

            return applied ? [...acc, { route: route.route, workstation: route.workstation }] : acc
        }, [])
    }, [workstation, hubs, search])

    const handleReassignRoute = (payload: { route: string; workstation: string }) => {
        setSearch('')
        openModal(ModalTypes.reassignWorkstationRouteModal, payload)
    }

    return (
        <Popup
            triggerComponent={() => (
                <Button size="small" rounded variant="tertiary" className={styles.reassignButton}>
                    <PlusIcon />
                </Button>
            )}
            placement="bottom-start"
            className={styles.popup}
            closeOnClick
        >
            <div className={styles.container}>
                <div className={styles.searchInputWrapper}>
                    <Input
                        value={search}
                        fullWidth
                        allowClear
                        placeholder="Search for route to control"
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
                <div className={styles.scrollContainer}>
                    {routesToReassign.map(({ route, workstation }, index) => (
                        <Button
                            key={index}
                            size="small"
                            variant="transparent"
                            onClick={() => handleReassignRoute({ route, workstation })}
                            className={styles.item}
                        >
                            <span>{route}</span>
                            <ChevronRight />
                        </Button>
                    ))}
                </div>
            </div>
        </Popup>
    )
}
